import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { Screen, TopBar, Loader } from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { labApi } from "@/data/api";

export default function LabDetail() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const toast = useToast();
  const { token } = useAuth();
  const { slug } = useParams();

  const [exp, setExp] = useState(null);
  const [order, setOrder] = useState([]);
  const [answers, setAnswers] = useState({});
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([labApi.get(slug), labApi.home()]).then(([e, home]) => { setExp(e); setOrder(home.experiments); }).catch(() => setExp(null)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!slug || !token) return;
    labApi.getAnswers(slug, token).then((r) => { setAnswers(r.answers ?? {}); setCompleted(r.completed); }).catch(() => {});
  }, [slug, token]);

  const { prev, next } = useMemo(() => {
    const idx = order.findIndex((e) => e.slug === slug);
    return { prev: idx > 0 ? order[idx - 1] : null, next: idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null };
  }, [order, slug]);

  const saveAnswers = async () => {
    if (!token || !slug) { toast.show("Log in to save your notes"); return; }
    try { await labApi.saveAnswers(slug, answers, token); toast.show("Saved ✨"); } catch { toast.show("Couldn't save — try again"); }
  };
  const toggleComplete = async () => {
    if (!token || !slug) return;
    const nextVal = !completed; setCompleted(nextVal);
    try { await labApi.setComplete(slug, nextVal, token); toast.show(nextVal ? "Marked as done ✨" : "Marked as not done"); } catch { setCompleted(!nextVal); }
  };

  if (loading) return <Screen><TopBar title="Experiment" /><Loader /></Screen>;
  if (!exp) return <Screen><TopBar title="Experiment" /><div style={{ padding: 40, textAlign: "center", color: colors.muted, fontFamily: fonts.text }}>Couldn't find that experiment.</div></Screen>;

  const renderBlock = (b, i) => {
    switch (b.type) {
      case "text":
        return <div key={i} style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>{(b.body ?? "").split("\n\n").map((p, j) => <div key={j} style={{ color: colors.onSurfaceSecondary, fontSize: 15, lineHeight: "22px", fontFamily: fonts.text }}>{p}</div>)}</div>;
      case "tip":
        return <div key={i} style={{ background: colors.brandTertiary, borderRadius: 14, padding: 14, marginBottom: 16, color: colors.onBrandTertiary, fontSize: 14, lineHeight: "21px", fontFamily: fonts.text }}>{b.body}</div>;
      case "list":
      case "examples":
        return (
          <div key={i} style={b.type === "examples" ? { background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 14, marginBottom: 16 } : { marginBottom: 16 }}>
            {b.title ? <div style={{ color: colors.onSurface, fontSize: 15, fontWeight: 800, marginBottom: 6, fontFamily: fonts.text }}>{b.title}</div> : null}
            {(b.items ?? []).map((it, j) => <div key={j} style={{ color: colors.onSurfaceSecondary, fontSize: 15, lineHeight: "22px", fontFamily: fonts.text }}>• {it}</div>)}
          </div>
        );
      case "compare":
        return (
          <div key={i} style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
              <div style={{ color: colors.muted, fontSize: 11, fontWeight: 800, marginBottom: 4, fontFamily: fonts.text }}>🤖 Robot</div>
              <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", fontFamily: fonts.text }}>{b.robot}</div>
            </div>
            <div style={{ background: colors.brandTertiary, borderRadius: 12, padding: 12 }}>
              <div style={{ color: colors.onBrandTertiary, fontSize: 11, fontWeight: 800, marginBottom: 4, fontFamily: fonts.text }}>💬 Human</div>
              <div style={{ color: colors.onBrandTertiary, fontSize: 14, lineHeight: "20px", fontFamily: fonts.text }}>{b.human}</div>
            </div>
            {b.note ? <div style={{ color: colors.muted, fontSize: 12, fontStyle: "italic", fontFamily: fonts.text }}>{b.note}</div> : null}
          </div>
        );
      case "labtask":
        return (
          <div key={i} style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.brand}`, borderRadius: 16, padding: 16, marginBottom: 16 }}>
            <div style={{ color: colors.onSurface, fontSize: 16, fontFamily: fonts.display, fontWeight: 700 }}>🧪 {b.title}</div>
            {b.prompt ? <div style={{ color: colors.onSurfaceSecondary, fontSize: 14, lineHeight: "20px", marginTop: 6, fontFamily: fonts.text }}>{b.prompt}</div> : null}
            {(b.fields ?? []).map((f) => (
              <div key={f.key} style={{ marginTop: 12 }}>
                <div style={{ color: colors.onSurface, fontSize: 13, fontWeight: 700, marginBottom: 6, fontFamily: fonts.text }}>{f.label}</div>
                <textarea data-testid={`lab-field-${f.key}`} value={answers[f.key] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [f.key]: e.target.value }))} placeholder="Your answer…" style={{ width: "100%", background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 14, color: colors.onSurface, fontFamily: fonts.text, minHeight: 60, resize: "vertical", outline: "none" }} />
              </div>
            ))}
            {b.note ? <div style={{ color: colors.muted, fontSize: 12, fontStyle: "italic", marginTop: 10, fontFamily: fonts.text }}>{b.note}</div> : null}
            <button data-testid="lab-save" className="pressable" onClick={saveAnswers} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: colors.brand, color: colors.onBrand, border: "none", borderRadius: 12, padding: "12px", marginTop: 14, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}>
              <Icon name="save" size={15} color={colors.onBrand} />Save my notes
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const navBtn = { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "12px", background: "transparent", cursor: "pointer", color: colors.brand, fontSize: 14, fontWeight: 700, fontFamily: fonts.text };

  return (
    <Screen>
      <TopBar title={`${exp.number} — ${exp.title}`} />
      <div className="ritzy-fade" style={{ padding: "12px 20px 40px" }}>
        {exp.subtitle ? <div style={{ color: colors.brandSecondary, fontSize: 15, fontWeight: 700, fontStyle: "italic", marginBottom: 16, lineHeight: "21px", fontFamily: fonts.text }}>{exp.subtitle}</div> : null}
        {(exp.blocks || []).map(renderBlock)}

        <button data-testid="lab-complete" className="pressable" onClick={toggleComplete} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: `1px solid ${colors.brand}`, borderRadius: 14, padding: "14px", marginTop: 4, cursor: "pointer", background: completed ? colors.brand : "transparent", color: completed ? colors.onBrand : colors.brand, fontSize: 15, fontWeight: 700, fontFamily: fonts.text }}>
          <Icon name={completed ? "check-circle" : "circle"} size={18} color={completed ? colors.onBrand : colors.brand} />{completed ? "Done — nice one" : "Mark as done"}
        </button>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 16 }}>
          {prev ? <button data-testid="lab-prev" onClick={() => navigate(`/lab/${prev.slug}`)} style={navBtn}><Icon name="arrow-left" size={16} color={colors.brand} />{prev.number}</button> : <div style={{ flex: 1 }} />}
          <button data-testid="lab-back" onClick={() => navigate("/lab")} style={{ width: 48, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${colors.border}`, borderRadius: 12, padding: "12px", background: "transparent", cursor: "pointer" }}><Icon name="grid" size={16} color={colors.brand} /></button>
          {next ? <button data-testid="lab-next" onClick={() => navigate(`/lab/${next.slug}`)} style={navBtn}>{next.number}<Icon name="arrow-right" size={16} color={colors.brand} /></button> : <div style={{ flex: 1 }} />}
        </div>

        <button data-testid="lab-vault" className="pressable" onClick={() => navigate("/content")} style={{ width: "100%", background: colors.brandTertiary, color: colors.onBrandTertiary, border: "none", borderRadius: 14, padding: "14px", marginTop: 16, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: fonts.text }}>🆘 My brain has no content today</button>
      </div>
    </Screen>
  );
}
